import { useState } from 'react';

import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { Form } from 'components/Form/Form';
import { useUserEnvironment } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import type { CmsApiSupportContactFormBlockData } from 'modules/cms/api/types/blocks/CmsApiSupportContactFormBlock';
import { postJson } from 'utils/http/postJson';
import { resetRecaptcha } from 'utils/recaptcha';

import {
  Container,
  Content,
  Headline,
  HeadlineBody,
  NoteSection,
  Panel,
} from './CmsSupportContactFormBlock.styled';
import { CmsSupportContactFormBlockContactForm } from './CmsSupportContactFormBlockContactForm';
import { CmsSupportContactFormBlockSummary } from './CmsSupportContactFormBlockSummary';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormValues = Record<string, any>;

const handleSubmit = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { handleError, updateSubmitting, values }: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  blockId: any,
  user: ApiUser,
  setSubmittedValues: (values: FormValues | null | undefined) => void,
) => {
  const { orgs = [] } = user || {};
  const { id: orgId = '' } =
    orgs.find((org) => org.name === values.orgName) ?? {};
  const valuesToSubmit = {
    orgId,
    ...values,
    attachmentIds: values.attachmentIds.filter(Boolean),
  };
  postJson(`/data/website/support-contact-form/${blockId}`, valuesToSubmit)
    .then(() => {
      updateSubmitting(false);
      setSubmittedValues(valuesToSubmit);
    })
    .catch((e) => {
      resetRecaptcha();
      handleError(e, {
        recaptchaKey: values.recaptchaKey + 1,
        gRecaptchaResponse: undefined,
      });
    });
};

type Props = {
  data: CmsApiSupportContactFormBlockData;
  isPageHeader?: boolean;
  blockId: string;
};

export function CmsSupportContactFormBlock({
  blockId,
  data,
  isPageHeader,
}: Props) {
  const { i18n } = useI18n();

  const { user } = useUserEnvironment();
  const initialValues = {
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    locale: CURRENT_LOCALE(),
    attachmentIds: [''],
  };
  const [submittedValues, setSubmittedValues] = useState<
    FormValues | null | undefined
  >();
  const { reasons } = data;
  const hasSubmittedWithFeedback =
    submittedValues && submittedValues.reason === 'FEEDBACK';

  if (submittedValues)
    return (
      <CmsSupportContactFormBlockSummary
        data={data}
        hasSubmittedWithFeedback={Boolean(hasSubmittedWithFeedback)}
      />
    );

  return (
    <Container
      $imageHandle={data.bgImage?.handle}
      $mimetype={data.bgImage?.mimetype}
    >
      <Content>
        <Panel>
          <Headline
            isPageHeader={Boolean(isPageHeader)}
            text={data.headline ?? 'Contact a Real Person at Idealist'}
          />
          {data.body ? (
            <HeadlineBody dangerouslySetInnerHTML={{ __html: data.body }} />
          ) : (
            <HeadlineBody>
              <p>
                Please fill out the contact form below. Alternatively, you can
                send us an email at{' '}
                <a href="mailto:support@idealist.org">support@idealist.org</a>{' '}
                or give us a call at{' '}
                <a href="tel:+1646-786-6886">646-786-6886</a>.
              </p>
            </HeadlineBody>
          )}
          {data.availability ? (
            <NoteSection
              label={i18n('Availability:')}
              text={data.availability}
            />
          ) : null}
          {data.address ? (
            <NoteSection label={i18n('Address:')} text={data.address} />
          ) : null}
          <Form
            analyticsTitle="[cms] Support contact block"
            usageContext="page"
            data-qa-id="support-contact-form"
            initialValues={
              initialValues as typeof initialValues & {
                reason?: string;
                subReason?: string;
              }
            }
            onSubmit={(formContext) =>
              // @ts-expect-error TS(2345): Argument of type 'ApiUser | undefined' is not assi... Remove this comment to see the full error message
              handleSubmit(formContext, blockId, user, setSubmittedValues)
            }
            render={({ getValue, updateValue }) => {
              const selectedReason = reasons.find(
                (r) => r.id === getValue('reason'),
              );
              const selectedSubReason =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                selectedReason &&
                selectedReason.subReasons &&
                selectedReason.subReasons.find(
                  (s) => s.id === getValue('subReason'),
                );
              return (
                <CmsSupportContactFormBlockContactForm
                  reasons={reasons}
                  selectedReason={selectedReason}
                  selectedSubReason={selectedSubReason}
                  updateValue={updateValue}
                />
              );
            }}
          />
        </Panel>
      </Content>
    </Container>
  );
}
